import { useCallback, useEffect, useMemo, useState } from "react";
import ExternalUserDetails from "./details";
import ExternalUserForm from "./form";
import { FormProvider, useForm } from "react-hook-form";
import {
  createExternalUserSchema,
  type ExternalUserFormData,
} from "@/schemas/users/external-user";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router";
import { useHeaderButton } from "@/contexts/header-button-context";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import {
  useGetExternalUser,
  useUpdateExternalUser,
  useActivateExternalUser,
  useInactivateExternalUser,
  useResetExternalUserPassword,
} from "@/queries/users/external-users.api";
import { cn } from "@/utils/theme";
import type { AxiosError } from "axios";
import ResetPasswordModal from "@/pages/users/details/edit/reset-password-modal";
import dayjs from "dayjs";
import { isLegalPersonDocument } from "@/utils/document-types";

const EditExternalUser = () => {
  const params = useParams<{ id: string }>();
  const [isUserActive, setIsUserActive] = useState(true);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    useState(false);

  const { mutateAsync: mutateUpdate, isPending: isLoadingUpdate } =
    useUpdateExternalUser(params.id!);
  const {
    mutateAsync: mutateToInactiveUser,
    isPending: isLoadingInactivateUser,
  } = useInactivateExternalUser(params.id!);
  const { mutateAsync: mutateToActiveUser, isPending: isLoadingActivateUser } =
    useActivateExternalUser(params.id!);
  const {
    mutateAsync: mutateResetPassword,
    isPending: isLoadingResetPassword,
  } = useResetExternalUserPassword(params.id!);

  const { data: userData, isLoading } = useGetExternalUser(params.id!);
  const userActivationHasChanged = isUserActive !== (userData?.status === 1);
  const isPreRegistered = userData?.registration_phase === "PRE_REGISTERED";

  const schema = useMemo(
    () => createExternalUserSchema(isPreRegistered),
    [isPreRegistered],
  );

  const methods = useForm<ExternalUserFormData>({
    mode: "onChange",
    reValidateMode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: "",
      last_name: "",
      document_type: "DUI",
      document_number: "",
      email: "",
      phone: {
        number: "",
        calling_code: "503",
        country: "SV",
      },
      birthdate: "",
      type: "APPLICANT",
    },
  });

  const [selectedUserType, setSelectedUserType] = useState<{
    label: string;
    value: string;
  } | null>(null);

  useEffect(() => {
    if (userData) {
      const phoneForInput = userData.phone ? userData.phone : "";

      const normalizedType = userData.type
        ? userData.type.toUpperCase()
        : "APPLICANT";

      const birthdateForInput = userData.birthdate
        ? new Date(userData.birthdate).toISOString()
        : "";

      methods.reset({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        commercial_name: userData.commercial_name || "",
        document_type: userData.document_type || "DUI",
        document_number: userData.document_number || "",
        email: userData.email || "",
        phone: phoneForInput,
        birthdate: birthdateForInput,
        type: normalizedType as
          "APPLICANT" | "FACILITATOR" | "REPRESENTATIVE" | "INSTITUTION",
      });

      setIsUserActive(userData.status === 1);

      setSelectedUserType(() => {
        const USER_TYPES = [
          { value: "APPLICANT", label: "Solicitante" },
          { value: "FACILITATOR", label: "Tramitador" },
          { value: "REPRESENTATIVE", label: "Representante" },
          { value: "INSTITUTION", label: "Institución" },
        ];
        const userType = USER_TYPES.find((t) => t.value === normalizedType);
        return userType ?? null;
      });
    }
  }, [userData, methods]);

  useEffect(() => {
    if (userData) {
      const isLegalPerson = isLegalPersonDocument(userData.document_type);
      const fullName = isLegalPerson
        ? userData.commercial_name
          ? `${userData.first_name} (${userData.commercial_name})`
          : userData.first_name
        : userData.last_name
          ? `${userData.first_name} ${userData.last_name}`
          : userData.first_name;
      document.title = fullName;
    }
  }, [userData]);

  const navigate = useNavigate();
  const { setComp } = useHeaderButton();
  const { showToast } = useToast();

  const dataHasChanged = useCallback(
    (currentData: ExternalUserFormData) => {
      const backendPhoneForInput = userData?.phone
        ? userData.phone.number.replace(/^\+503\s*/, "").replace(/-/g, " ")
        : "";

      const currentBirthdate = currentData.birthdate
        ? new Date(currentData.birthdate).toISOString()
        : "";
      const backendBirthdate = userData?.birthdate
        ? new Date(userData.birthdate).toISOString()
        : "";

      return (
        currentData.first_name !== userData?.first_name ||
        currentData.last_name !== userData?.last_name ||
        currentData.email !== userData?.email ||
        currentData.phone.number !== backendPhoneForInput ||
        currentData.document_type !== userData?.document_type ||
        currentData.document_number !== userData?.document_number ||
        currentBirthdate !== backendBirthdate ||
        currentData.type !== userData?.type
      );
    },
    [userData],
  );

  const data = methods.watch();
  const {
    formState: { isValid },
  } = methods;
  const isButtonDisabled = useMemo(() => {
    // Deshabilita si no hay cambios, no hay cambio de activación, o el formulario no es válido
    return (!dataHasChanged(data) && !userActivationHasChanged) || !isValid;
  }, [data, userActivationHasChanged, dataHasChanged, isValid]);

  useEffect(() => {
    setComp(
      <>
        <Button
          variant="quiet"
          size="xs"
          type="button"
          onClick={() => navigate(-1)}
        >
          Cancelar
          <Icon type="far" name="times" />
        </Button>
        <Button
          variant="default"
          size="xs"
          form="external-user-form"
          type="submit"
          disabled={
            isButtonDisabled ||
            isLoadingUpdate ||
            isLoadingActivateUser ||
            isLoadingInactivateUser ||
            isLoading
          }
        >
          Guardar cambios
          <Icon
            type="fas"
            name={
              isLoadingUpdate ||
              isLoadingActivateUser ||
              isLoadingInactivateUser
                ? "spinner-third"
                : "save"
            }
            className={cn(
              "text-current",
              (isLoadingUpdate ||
                isLoadingActivateUser ||
                isLoadingInactivateUser) &&
                "animate-spin",
            )}
          />
        </Button>
      </>,
    );
    return () => {
      setComp(null);
    };
  }, [
    navigate,
    setComp,
    isLoadingUpdate,
    isButtonDisabled,
    isLoadingActivateUser,
    isLoadingInactivateUser,
    isLoading,
  ]);

  const onSubmit = async (data: ExternalUserFormData, description: string) => {
    try {
      if (dataHasChanged(data)) {
        let birthdateFormatted = "";
        if (data.birthdate) {
          try {
            const date = new Date(data.birthdate);
            if (!Number.isNaN(date.getTime())) {
              birthdateFormatted = dayjs(date).format("YYYY-MM-DD");
            }
          } catch (e) {
            console.error("Error parsing birthdate:", e);
          }
        }

        const dataToSend = {
          ...data,
          birthdate: birthdateFormatted,
        };
        await mutateUpdate(dataToSend);
      }

      if (userActivationHasChanged) {
        if (isUserActive) {
          await mutateToActiveUser({ description });
          showToast({
            title: "Usuario activado",
            description: "El usuario externo ha sido activado correctamente.",
            options: {
              type: "success",
              duration: 4000,
            },
          });
          navigate("/usuarios?user-tab=externos");
          return;
        }
        await mutateToInactiveUser({ description });
        showToast({
          title: "Usuario desactivado",
          description: "El usuario externo ha sido desactivado exitosamente.",
          options: {
            type: "success",
            duration: 5000,
            customIcon: (
              <Avatar size="xs" shape="square">
                <AvatarFallback color="danger" shape="square">
                  <Icon type="fas" name="user-minus" />
                </AvatarFallback>
              </Avatar>
            ),
          },
        });
      } else {
        showToast({
          title: "Usuario actualizado",
          description: "El usuario externo ha sido actualizado correctamente.",
          options: {
            type: "success",
            duration: 4000,
          },
        });
      }
      navigate("/usuarios?user-tab=externos");
    } catch (e) {
      const error = e as AxiosError<{
        message: string;
        errors?: {
          [key: string]: string[];
        };
      }>;

      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        if (errors) {
          Object.keys(errors).forEach((key) => {
            if (key === "email") {
              methods.setError("email", {
                type: "manual",
                message:
                  errors.email[0] || "El correo electrónico ya está registrado",
              });
            }
            if (key === "document_number") {
              methods.setError("document_number", {
                type: "manual",
                message:
                  errors.document_number[0] ||
                  "El documento ya está registrado",
              });
            }
          });
        }
      }

      showToast({
        title: "Error al actualizar el usuario externo",
        description:
          error.response?.data?.message ||
          "Revisa los datos ingresados e inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 4000,
        },
      });
    }
  };

  const toggleUserActivation = (checked: boolean) => {
    setIsUserActive(checked);
  };

  const onResetPassword = async () => {
    setIsResetPasswordModalOpen(true);
  };

  const handleResetPasswordConfirm = async () => {
    try {
      await mutateResetPassword();
      showToast({
        title: "Cambio de contraseña solicitado",
        description:
          "El usuario recibirá una solicitud para completar el proceso de restablecimiento de contraseña.",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square">
                <Icon type="fas" name="lock" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch {
      showToast({
        title: "Error al intentar restablecer contraseña",
        description:
          "La solicitud de restablecimiento de contraseña no pudo ser enviado, inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 4000,
        },
      });
    } finally {
      setIsResetPasswordModalOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Icon
          type="far"
          name="spinner-third"
          className="animate-spin text-4xl text-action"
        />
      </div>
    );
  }

  return (
    <>
      <ResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        isLoading={isLoadingResetPassword}
        onClose={() => setIsResetPasswordModalOpen(false)}
        onAccept={handleResetPasswordConfirm}
      />
      <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3">
        <FormProvider {...methods}>
          <ExternalUserDetails
            selectedUserType={selectedUserType}
            isUserActive={isUserActive}
            isUserBlocked={false}
            registrationPhase={userData?.registration_phase}
            toggleUserActivation={toggleUserActivation}
            onResetPassword={onResetPassword}
            onUnblockUser={() => {}}
            userId={params.id || ""}
          />
          <ExternalUserForm
            userId={params.id}
            userActivationHasChanged={userActivationHasChanged}
            isUserActive={isUserActive}
            isPreRegistered={isPreRegistered}
            setSelectedUserType={setSelectedUserType}
            onSubmit={onSubmit}
            setIsUserActive={setIsUserActive}
          />
        </FormProvider>
      </div>
    </>
  );
};

export default EditExternalUser;
